import React, { useState, useEffect, useContext } from "react";
import { Grid, Typography } from "@material-ui/core/";
import moment from "moment";
import { EWContext } from "../../../context/EWContext";
import {
   createSkeduloJob,
   rescheduleSkeduloJob,
} from "../../../services/services";
import {
   DATE_TIME_FORMAT,
   SHORT_DATE_FORMAT,
   SUBTITLE_TEXT_SUMMARY,
   COORDINATES_FIELD,
   LANGUAGE_TEXT,
} from "../../../constants";
import SummaryValue from "../SummaryContent/SummaryValue/SummaryValue";
import SummaryQuestion from "../SummaryContent/SummaryQuestion/SummaryQuestion";
import StepperNavigationButtons from "../../StepperNavigationButtons/StepperNavigationButtons";
import EligibilitySubtitle from "../../forms/FormBuilder/EligibilitySubtitle/EligibilitySubtitle";

export default function ScheduleAppointmentSummary({ handleNext, handleBack }) {
   const [accountState, setAccountState] = useContext(EWContext);
   const {
      answers,
      productInfo,
      skeduloInfo,
      skeduloJobData,
      baseUrl,
      customer,
      appReference,
      appointmentAllocationId,
      appointmentId,
      latitude,
      longitude,
      conditionSpecificBehaviors,
   } = accountState;

   const { schedule } = answers;
   const { buttonNameOnSummary } = conditionSpecificBehaviors
      ? conditionSpecificBehaviors
      : false;
   const [loading, setLoading] = useState(false);
   const { startDate, endDate, regionId, jobDuration, availableResources } =
      skeduloJobData;
   const addressCoordinates = answers.validate?.find((answer) =>
      COORDINATES_FIELD.includes(answer.id)
   ).extraValues || { latitude, longitude };
   const [schedulingError, setSchedulingError] = useState({
      status: false,
      message: "",
      disableConfirmButton: false,
   });

   useEffect(() => {
      if (
         answers.schedule.find((answer) => answer.id === "jobId") &&
         answers.schedule.find((answer) => answer.id === "allocationId")
      ) {
         handleNext();
      }
   }, [answers.schedule, handleNext]);

   const sizes = {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
      xl: 6,
   };

   const getAppointmentDate = () => {
      const filteredItems = answers.schedule.filter(
         (answer) => answer.id === "scheduleAppointment"
      );
      return moment.parseZone(filteredItems[0].value).format(SHORT_DATE_FORMAT);
   };

   const getStartTime = () => {
      const filteredItems = answers.schedule.filter(
         (answer) => answer.id === "scheduleAppointment"
      );
      return moment.parseZone(filteredItems[0].value).format(DATE_TIME_FORMAT);
   };

   const getNotes = () => {
      const filteredItems = answers.schedule.filter(
         (answer) => answer.id === "dateNotes"
      );
      return filteredItems[0].value;
   };

   const answersSchedule = [
      {
         label:
            accountState?.conditionSpecificBehaviors?.translateText &&
            accountState?.storeInfo?.languageCode
               ? LANGUAGE_TEXT[accountState?.storeInfo?.languageCode]
                    ?.appointment_type
               : "Appointment Type:",
         value:
            accountState?.conditionSpecificBehaviors?.translateText &&
            conditionSpecificBehaviors?.cr_review_label
               ? conditionSpecificBehaviors.cr_measure_appointment_name
               : productInfo.appointmentJobType,
      },
      {
         label:
            accountState?.conditionSpecificBehaviors?.translateText &&
            accountState?.storeInfo?.languageCode
               ? LANGUAGE_TEXT[accountState?.storeInfo?.languageCode]
                    ?.appointment_date
               : "Appointment Date:",
         value: getAppointmentDate(),
      },
      {
         label:
            accountState?.conditionSpecificBehaviors?.translateText &&
            accountState?.storeInfo?.languageCode
               ? LANGUAGE_TEXT[accountState?.storeInfo?.languageCode]
                    ?.start_time
               : "Start Time:",
         value: getStartTime(),
         showOnSummary: accountState?.show_start_and_estimated_time_on_summary,
      },
      {
         label:
            accountState?.conditionSpecificBehaviors?.translateText &&
            accountState?.storeInfo?.languageCode
               ? LANGUAGE_TEXT[accountState?.storeInfo?.languageCode]
                    ?.estimated_appointment_time
               : "Estimated Appointment Time:",
         value: `${jobDuration} minutes`,
         showOnSummary: accountState?.show_start_and_estimated_time_on_summary,
      },
      {
         label: "Instruction Notes:",
         value: getNotes(),
      },
   ];

   const updateJob = async () => {
      setLoading(true);
      if (
         answers.schedule.find((answer) => answer.id === "jobId") &&
         answers.schedule.find((answer) => answer.id === "allocationId")
      ) {
         setLoading(false);
         handleNext();
      } else {
         const variables = {
            rescheduleJob: {
               jobId: appointmentId,
               start: startDate,
               end: endDate,
               allocationId: appointmentAllocationId,
               notes: getNotes(),
               availableResources,
            },
         };

         rescheduleSkeduloJob(baseUrl, customer, variables)
            .then((job) => {
               schedule[0].extraValues.PIN = job[0].data.schema.PIN;

               setAccountState((prevAccountState) => ({
                  ...prevAccountState,
                  answers: {
                     ...prevAccountState.answers,
                     schedule: [
                        ...schedule,
                        {
                           id: "jobId",
                           value: job[0].data.schema.updateJobs,
                        },
                        {
                           id: "allocationId",
                           value: job[0].data.schema.insertJobAllocations,
                        },
                     ],
                  },
               }));
               setLoading(false);
            })
            .catch((error) => {
               console.error(error.message);
               setSchedulingError((prevSchedulingError) => ({
                  ...prevSchedulingError,
                  status: true,
                  message: error.message,
                  disableConfirmButton: true,
               }));
               setLoading(false);
            });
      }
   };

   const createNewJob = async () => {
      setLoading(true);
      if (
         answers.schedule.find((answer) => answer.id === "jobId") &&
         answers.schedule.find((answer) => answer.id === "allocationId")
      ) {
         setLoading(false);
         handleNext();
      } else {
         const address = answers.validate.find(
            (el) => el.id === "address"
         ).value;
         const city = answers.validate.find((el) => el.id === "city").value;
         const state = answers.validate.find((el) => el.id === "state").value;
         const zip = answers.validate.find((el) => el.id === "zip").value;
         const jobAddress = `${address}, ${city}, ${state} ${zip}`;
         const notes = getNotes();
         const description = answers.validate
            .filter((el) => el.descriptionSkedulo)
            .map((el) => el.value);

         const variables = {
            job: {
               title: `${skeduloInfo.programName} ${productInfo.appointmentJobType}`,
               address: jobAddress,
               regionId,
               start: startDate,
               end: endDate,
               duration: Number(jobDuration),
               notes,
               latitude: addressCoordinates?.latitude || "",
               longitude: addressCoordinates?.longitude || "",
               description,
               availableResources,
            },
         };

         // TODO: handle job creation failed...
         createSkeduloJob(baseUrl, customer, variables)
            .then((job) => {
               schedule[0].extraValues.PIN = job[0].data.schema.PIN;

               setAccountState((prevAccountState) => ({
                  ...prevAccountState,
                  answers: {
                     ...prevAccountState.answers,
                     schedule: [
                        ...schedule,
                        {
                           id: "jobId",
                           value: job[0].data.schema.insertJobs,
                        },
                        {
                           id: "allocationId",
                           value: job[0].data.schema.insertJobAllocations,
                        },
                     ],
                  },
               }));
               setLoading(false);
            })
            .catch((error) => {
               console.error(error.message);
               setSchedulingError((prevSchedulingError) => ({
                  ...prevSchedulingError,
                  status: true,
                  message: error.message,
                  disableConfirmButton: true,
               }));
               setLoading(false);
            });
      }
   };

   return (
      <>
         <Typography
            variant="h4"
            gutterBottom
            style={{ margin: "35px 0 10px 15px" }}
         >
            {conditionSpecificBehaviors?.summaryTitleCheck
               ? conditionSpecificBehaviors?.summaryTitleName
               : "Energy Assessment Appointment Summary"}
         </Typography>
         <EligibilitySubtitle
            text={
               conditionSpecificBehaviors?.cr_review_subtitle
                  ? `${conditionSpecificBehaviors.cr_SUBTITLE_TEXT_SUMMARY}`
                  : `${SUBTITLE_TEXT_SUMMARY}`
            }
         />
         <Grid container spacing={2}>
            {answersSchedule.map((answer, index) => {
               if (
                  answer.showOnSummary === true ||
                  answer.showOnSummary === undefined
               ) {
                  return answer.value === true ? (
                     <SummaryQuestion key={answer.id} label={answer.label} />
                  ) : (
                     <SummaryValue
                        sizes={sizes}
                        key={index}
                        label={answer.label}
                        value={answer.value}
                     />
                  );
               } else return null;
            })}
         </Grid>

         {schedulingError.status && (
            <Typography
               variant="caption"
               gutterBottom
               style={{
                  margin: "15px 0px",
               }}
            >
               {schedulingError.message}
            </Typography>
         )}

         <StepperNavigationButtons
            handleBack={handleBack}
            handleNext={appReference ? updateJob : createNewJob}
            loading={loading}
            nextButtonDisabled={schedulingError.disableConfirmButton}
            buttonNameOnSummary={buttonNameOnSummary || false}
         />
      </>
   );
}
